<template>
  <div class="p-box__body">
    <div class="specialHour__header">
      <div class="specialHour__datepicker">
        <DatePicker
          :initial-dates="new Date(specialHour.date)"
          :is-single="true"
          :disabled-dates="{ from: null, to: null }"
          opens="right"
          @input="(val) => changeDate(specialHour,val)"
        />
      </div>
      <span class="c-radio__item">
        <input
          :id="`${specialHour.wday}_close_checkbox_${i}`"
          :checked="!specialHour.noChange && specialHour.periods.length === 0"
          type="radio"
          class="c-radio__input"
          @change="removePeriods(specialHour)"
        >
        <label
          class="c-radio__label"
          :for="`${specialHour.wday}_close_checkbox_${i}`"
        >{{ $t('models.storeSpecialHour.regularHoliday') }}</label>
      </span>
      <span class="c-radio__item">
        <input
          :id="`${specialHour.wday}_open_checkbox_${i}`"
          :checked="!specialHour.noChange && specialHour.periods.length !== 0 && !is24h(specialHour)"
          type="radio"
          class="c-radio__input"
          @change="createPeriod(specialHour)"
        >
        <label
          class="c-radio__label"
          :for="`${specialHour.wday}_open_checkbox_${i}`"
        >{{ $t('models.storeSpecialHour.businessDay') }}</label>
      </span>
      <span class="c-radio__item">
        <input
          :id="`${specialHour.wday}_24h_open_checkbox_${i}`"
          :checked="!specialHour.noChange && specialHour.periods.length !== 0 && is24h(specialHour)"
          type="radio"
          class="c-radio__input"
          @change="set24h(specialHour)"
        >
        <label
          class="c-radio__label"
          :for="`${specialHour.wday}_24h_open_checkbox_${i}`"
        >{{ $t('models.storeSpecialHour.open24hours') }}</label>
      </span>
    </div>
    <div class="specialHour__main">
      <div v-if="specialHour.periods.length !== 0 && !is24h(specialHour)" class="specialHour__times">
        <div v-for="(period, k) in specialHour.periods" :key="`${specialHour.wday}_${k}`" class="specialHour__form">
          <fieldset class="specialHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__5 p-formGrid__item__l__5">
            <label class="c-formLabel">{{ $t('labels.time.start') }}</label>
            <ValidationProvider
              v-slot="{ errors, value, handleChange }"
              :key="`start_${i}_${k}`"
              v-model="period.openTime"
              :vid="`specialHourStartForm_${i}_${k}`"
              :rules="`required|time_format:@specialHourEndForm_${i}_${k}|after_pre_time:@specialHourEndForm_${i}_${k - 1}`"
              :name="$t('labels.time.start')"
            >
              <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.time.start')" @input="handleChange">
              <ValidationError :error="errors[0]" />
            </ValidationProvider>
          </fieldset>
          <fieldset class="specialHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__5 p-formGrid__item__l__5">
            <label class="c-formLabel">{{ $t('labels.time.end') }}</label>
            <ValidationProvider
              v-slot="{ errors, value, handleChange }"
              :key="`end_${i}_${k}`"
              v-model="period.closeTime"
              :vid="`specialHourEndForm_${i}_${k}`"
              :rules="`required|time_format:@specialHourStartForm_${i}_${k}`"
              :name="$t('labels.time.end')"
            >
              <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.time.end')" @input="handleChange">
              <ValidationError :error="errors[0]" />
            </ValidationProvider>
          </fieldset>
          <fieldset class="specialHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__2 p-formGrid__item__l__2">
            <div v-if="specialHour.periods.length > 1" class="specialHour-period-remove">
              <span class="specialHour-period-remove__button" @click="removePeriod(specialHour, k)">
                <Icon name="iconCross" class="p-stores__action__icon" />
                {{ $t('labels.delete') }}
              </span>
            </div>
          </fieldset>
        </div>
        <div class="specialHour-period__add c-button c-button--icon">
          <div class="c-button__item c-button__item--primary" @click="addPeriod(specialHour)">
            <Icon name="iconPlusRed" class="c-button__icon" />
            <span>{{ $t('labels.add', { value: $t('models.storeSpecialHour.modelName') }) }}</span>
          </div>
        </div>
      </div>
      <div v-if="displayDeleteBtn" :class="{ 'specialHour__remove': specialHour.periods.length > 1 }">
        <div class="specialHour-period-remove">
          <span class="specialHour-period-remove__button" @click="$emit('remove-special-hour', i)">
            <Icon name="iconCross" class="p-stores__action__icon" />
            {{ $t('components.SpecialHourCard.deleteSpecialHour') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ValidationError from './ValidationError'
import DatePicker from './DatePicker'
import Icon from './Icon'
import { basicDateFormat } from '../modules/date'

export default {
  name: 'SpecialHourCard',
  components: {
    DatePicker,
    ValidationError,
    Icon
  },
  props: {
    specialHour: {
      type: Object,
      required: true,
    },
    i: {
      type: Number,
      required: true,
    },
    displayDeleteBtn: {
      type: Boolean,
      required: true,
    }
  },
  emits: [
    'remove-special-hour',
  ],
  methods: {
    is24h(specialHour) {
      if (specialHour.periods.length >= 2) return false // 24時間営業の時はperiods.lengthは1になるため、それ以上あればfalse

      const period = specialHour.periods[0]
      return period?.openTime === '00:00' && period?.closeTime === '24:00'
    },
    set24h(val) {
      val.noChange = false
      val.periods = [{openTime: '00:00', closeTime: '24:00'}]
    },
    createPeriod(val) {
      val.noChange = false
      val.periods = [{openTime: '', closeTime: '', foodOption: { lastOrderTime: '', kind: '' }}]
    },
    removePeriods(val) {
      val.noChange = false
      val.periods = []
    },
    addPeriod(val) {
      val.periods.push({openTime: '', closeTime: '', foodOption: { lastOrderTime: '', kind: '' }})
    },
    changeDate(specialHour, newVal) {
      specialHour.date = basicDateFormat(newVal)
    },
    removePeriod(val, key) {
      val.periods.splice(key, 1)
    },
    displayWeekday(weekday) {
      return this.$t('labels.weekday.' + weekday)
    },
  }
}
</script>

<style lang="scss" scoped>
.specialHour {
  &__header {
    vertical-align: baseline;
    display: flex;
    align-items: baseline;
  }
  &__main {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__remove {
    width: 30%;
    text-align: end;
  }
  &__times {
    width: 70%;
  }
  &__datepicker {
    width: 359px;
  }
  &__label {
    padding: 0 0 12px;
    font-weight: bold;
    font-size: 18px;
    display: inline-block;
    width: 70px;
  }
  &__form {
    display: inline-flex;
    flex-wrap: wrap;
    width: 100%;
    margin: 24px -8px 0;
  }
  &-period {
    &__form {
      padding: 0 8px 8px;
    }
    &-remove {
      padding: 32px 0 12px 0;
      &__button {
        cursor: pointer;
        display: inline-flex;
        flex-wrap: wrap;
        align-items: center;
      }
    }
    &__add {
      margin-top: 0;
    }
  }
}

</style>
